import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { endOfDay, format, startOfDay, startOfMonth } from "date-fns";

import { ISO8601_DATE_FORMAT } from "@constants";

import type { IFarmsDto } from "../../../modules/farms/shared/dtos/farms";
import { farmsApi } from "../../../modules/farms/store/farms.rtkq";
import { TEkoCropApiFilter } from "../../../modules/filter/shared/utils/eko-crop-filter-adapter";
import { addNotificationAction } from "../../../modules/notifications";
import { NotificationSeverity } from "../../../modules/notifications/shared/enums/notification-severity";
import { NotificationType } from "../../../modules/notifications/shared/enums/notification-type";
import { verificationService } from "../../../modules/verification/shared/services/verification.service";
import type { IErrorResponseDto } from "../../../shared/dtos/error-response.dto";
import { RootState } from "../../../store-types";
import { IAssetUsageRecordDto } from "../shared/dtos/asset-usage-record.dto";
import { AssetUsageRecordListSchema } from "../shared/dtos/asset-usage-record.schema";
import { AssetUsageRecordStatus } from "../shared/enums/asset-usage-record-status";
import { DatePresetOption } from "../shared/enums/date-preset-option";
import { TAssetUsageApiFilter } from "../shared/interfaces/asset-usage-api-filter";
import { TAssetUsageFilter } from "../shared/interfaces/asset-usage-filter";
import { IAssetUsageRecordsState } from "../shared/interfaces/asset-usage-records-state";
import { AssetUsageRecord } from "../shared/models/asset-usage-record";
import { assetUsageRecordsService } from "../shared/services/asset-usage-records.service";
import { getConfigurationState } from "../shared/utils/get-configuration-state";
import { getTitleByStatus } from "../shared/utils/get-title-by-status";
import {
  getAssetsWriteOffClosedPeriodMessage,
  isWriteOffAllowedForFinishDate,
} from "../shared/utils/is-write-off-allowed-for-period";

import { getSelected } from "./asset-usage-records.selector";
import {
  ASSETS_WRITE_OFF_DEADLINE_DAY,
  ASSET_USAGE_RECORDS_MODULE_NAME,
  IS_WRITE_OFF_ERROR_MODAL_ENABLED,
} from "./constants";

const initialFilterState: TAssetUsageFilter = {
  datePreset: DatePresetOption.Period,
  fromDate: format(startOfDay(startOfMonth(new Date())), ISO8601_DATE_FORMAT),
  toDate: format(endOfDay(new Date()), ISO8601_DATE_FORMAT),
  farmIds: [],
  statusIds: [],
  techOperationGroups: [],
  techOperationSubGroups: [],
  cropTypeIds: [],
  assetCategories: [],
  farmlandIds: [],
  materiallyAccountableEmployees: [],
};

const initialState: IAssetUsageRecordsState = {
  details: null,
  isLoading: false,
  currentRequestId: null,
  isFilterOpen: false,
  list: [],
  listCount: 0,
  filter: initialFilterState,
  configuration: getConfigurationState(),
  selected: [],
};

const assetUsageRecordStatuses = Object.values(AssetUsageRecordStatus);

const isAssetUsageRecordStatus = (value: string): value is AssetUsageRecordStatus =>
  assetUsageRecordStatuses.includes(value as AssetUsageRecordStatus);

const getWriteOffErrorReasonWithStatusTitle = (reason: string): string =>
  isAssetUsageRecordStatus(reason) ? getTitleByStatus(reason) : reason;

const getWriteOffErrorReason = (error: unknown): string => {
  const restApiError = (error as IErrorResponseDto)?.error;

  if (restApiError?.message) {
    return getWriteOffErrorReasonWithStatusTitle(restApiError.message);
  }

  if (error instanceof Error && error.message) {
    return getWriteOffErrorReasonWithStatusTitle(error.message);
  }

  if (typeof error === "string" && error) {
    return getWriteOffErrorReasonWithStatusTitle(error);
  }

  return "Причину ошибки определить не удалось";
};

export const assetUsageRecordsSlice = createSlice({
  name: ASSET_USAGE_RECORDS_MODULE_NAME,
  initialState,
  reducers: {
    setDetails(state, action: PayloadAction<AssetUsageRecord | null>): void {
      state.details = action.payload;
    },
    toggleIsFilterOpen(state): void {
      state.isFilterOpen = !state.isFilterOpen;
    },
    setIsFilterOpen(state, action: PayloadAction<boolean>): void {
      state.isFilterOpen = action.payload;
    },
    setListAction(state, action: PayloadAction<AssetUsageRecord[]>): void {
      state.list = action.payload;
    },
    setListCountAction(state, action: PayloadAction<number>): void {
      state.listCount = action.payload;
    },
    setSelectedAction(state, action: PayloadAction<AssetUsageRecord[]>): void {
      state.selected = action.payload;
    },
    addSelectedAction(state, action: PayloadAction<AssetUsageRecord[]>): void {
      const itemsToAdd = action.payload;
      const existedIds = state.selected.map((item) => item.id);

      for (let i = 0; i < itemsToAdd.length; i++) {
        if (existedIds.includes(itemsToAdd[i].id)) {
          continue;
        }
        state.selected.push(itemsToAdd[i]);
      }
    },
    removeSelectedAction(state, action: PayloadAction<AssetUsageRecord[]>): void {
      const idsToRemove = action.payload.map((item) => item.id);

      for (let i = state.selected.length - 1; i >= 0; i--) {
        if (!idsToRemove.includes(state.selected[i].id)) {
          continue;
        }
        state.selected.splice(i, 1);
      }
    },
    toggleSelectedAction(state, action: PayloadAction<AssetUsageRecord[]>): void {
      const selectedItems = new Map<string, AssetUsageRecord>(
        action.payload.map((obj) => [obj.id, obj])
      );

      // remove already existed items
      for (let i = state.selected.length - 1; i >= 0; i--) {
        const existingId = state.selected[i].id;
        if (selectedItems.has(existingId)) {
          state.selected.splice(i, 1);
          selectedItems.delete(existingId);
        }
      }
      // add new ones
      state.selected.push(...Array.from(selectedItems.values()));
    },
    setFilterAction(state, action: PayloadAction<TAssetUsageFilter>): void {
      state.filter = action.payload;
    },
    resetFilterAction(state): void {
      state.filter = initialFilterState;
    },
    resetSelectedAction(state): void {
      state.selected = initialState.selected;
    },
    toggleVisibleColumn(state, action: PayloadAction<keyof AssetUsageRecord>): void {
      const value = action.payload;
      const { configurable, columns, selected } = state.configuration;
      if (!configurable.includes(value)) {
        return;
      }

      const item = columns.find((column) => column.id === value);
      if (!item) {
        return;
      }

      const index = selected.indexOf(value);
      if (index !== -1) {
        selected.splice(index, 1);
      } else {
        selected.push(value);
      }

      state.configuration.selected = selected;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchAssetUsageRecordsAction.fulfilled, (state, action) => {
        if (state.currentRequestId !== action.meta.requestId) {
          return;
        }
        state.isLoading = false;
        state.currentRequestId = null;
      })
      .addCase(fetchAssetUsageRecordsAction.pending, (state, action) => {
        state.isLoading = true;
        state.currentRequestId = action.meta.requestId;
      })
      .addCase(fetchAssetUsageRecordsAction.rejected, (state, action) => {
        if (state.currentRequestId !== action.meta.requestId) {
          return;
        }
        state.isLoading = false;
        state.currentRequestId = null;
      })
      .addCase(prepareAssetsToSendAction.fulfilled, (state, _) => {
        state.isLoading = false;
      })
      .addCase(prepareAssetsToSendAction.pending, (state, _) => {
        state.isLoading = true;
      })
      .addCase(prepareAssetsToSendAction.rejected, (state, _) => {
        state.isLoading = false;
      });
  },
});

export const assetUsageRecordsReducer = assetUsageRecordsSlice.reducer;
export const {
  setDetails,
  setListAction,
  setSelectedAction,
  toggleIsFilterOpen,
  setListCountAction,
  addSelectedAction,
  removeSelectedAction,
  toggleSelectedAction,
  setFilterAction,
  resetFilterAction,
  toggleVisibleColumn,
  resetSelectedAction,
} = assetUsageRecordsSlice.actions;

let abortController: AbortController | null = null;
export const fetchAssetUsageRecordsAction = createAsyncThunk<
  void,
  TEkoCropApiFilter<TAssetUsageApiFilter>
>(
  `${ASSET_USAGE_RECORDS_MODULE_NAME}/fetchAssetUsageRecordsAction`,
  async (filter, { dispatch }) => {
    // prevent multiple calls
    if (abortController) {
      abortController.abort();
    }
    abortController = new AbortController();

    let dtos: IAssetUsageRecordDto[];
    let count: number;
    try {
      [dtos, count] = await Promise.all([
        assetUsageRecordsService.list(filter, { signal: abortController.signal }).then(
          verificationService.check({
            cacheId: "AssetUsageRecordListSchema",
            schema: AssetUsageRecordListSchema,
            message: "Невалидный ответ сервера (AssetUsageRecord)",
          })
        ),
        assetUsageRecordsService.listCount(filter, { signal: abortController.signal }),
      ]);
    } catch (error) {
      if (error instanceof Error) {
        if (error.name === "AbortError") {
          return;
        }
      }
      dispatch(
        addNotificationAction({
          type: NotificationType.Snack,
          severity: NotificationSeverity.Error,
          message: "Ошибка получения актов списания",
          error,
        })
      );
      throw error;
    }
    abortController = null;

    const models = dtos.map((dto) => {
      const model = new AssetUsageRecord(dto.id);
      try {
        model.updateFromDto(dto);
      } catch (error) {
        dispatch(
          addNotificationAction({
            type: NotificationType.Snack,
            severity: NotificationSeverity.Warning,
            message: "Ошибка в модели акта списания",
            error,
          })
        );
      }
      return model;
    });

    dispatch(setListAction(models));
    dispatch(setListCountAction(count));
  }
);

export const prepareAssetsToSendAction = createAsyncThunk<
  boolean,
  void,
  { state: RootState }
>(
  `${ASSET_USAGE_RECORDS_MODULE_NAME}/prepareAssetsToSendAction`,
  async (_, { dispatch, getState }) => {
    const selectedItems = getSelected(getState());
    // TODO: extend validation filter
    // removed via https://tracker.yandex.ru/DEVELOPMENT-4014
    // .filter((item) =>
    //   [AssetUsageRecordStatus.Created, AssetUsageRecordStatus.Expired].includes(
    //     item.status
    //   )
    // );
    if (!selectedItems.length) {
      dispatch(
        addNotificationAction({
          type: NotificationType.Snack,
          severity: NotificationSeverity.Warning,
          message: "Доступных для списания записей не выбрано",
        })
      );
      return false;
    }

    // DEVELOP-5411: запрет списания по АР из закрытого периода
    const today = new Date();
    const farms: IFarmsDto[] =
      farmsApi.endpoints.fetchFarms.select(undefined)(getState()).data ?? [];

    const getCutoffDayForFarm = (farmId: string): number => {
      const lowerCaseFarmId = farmId.toLowerCase();
      const farm = farms.find((f) => f.id.toLowerCase() === lowerCaseFarmId);
      return farm?.writeOffPeriodCutoffDay ?? ASSETS_WRITE_OFF_DEADLINE_DAY;
    };

    const firstForbiddenItem = selectedItems.find((item) => {
      const cutoffDay = getCutoffDayForFarm(item.dto.farmland.farmId);
      return !isWriteOffAllowedForFinishDate(item.dateFinish, cutoffDay, today);
    });

    const hasClosedPeriodRecord = !!firstForbiddenItem;

    if (hasClosedPeriodRecord) {
      const messageDay = getCutoffDayForFarm(firstForbiddenItem.dto.farmland.farmId);

      dispatch(
        addNotificationAction({
          type: NotificationType.Confirm,
          confirmOnly: true,
          severity: NotificationSeverity.Error,
          title: "Операция списания недоступна",
          message: getAssetsWriteOffClosedPeriodMessage(messageDay).replace(
            /\n/g,
            "\n\n"
          ),
          cb: () => void 0,
        })
      );
      return false;
    }

    // TODO: need to use a transaction when we try to patch models (group operations should use one request)
    const prepareAssetsToSendCb = async () => {
      let updateCount = 0;
      try {
        for (const item of selectedItems) {
          item.setStatus(AssetUsageRecordStatus.PreparedToSend);
        }
        const assetUsageRecords = await assetUsageRecordsService.prepareToSend(
          selectedItems.map((item) => item.id)
        );

        // TODO https://tracker.yandex.ru/DEVELOPMENT-4395
        // удалить этот  код когда будет выполнен фикс на бэкенде
        const allIdsAreValid = assetUsageRecords.every((record) => !!record.id);
        if (!allIdsAreValid) {
          throw new Error("В списке на списание присутствуют акты с невалидным ID");
        }

        updateCount = selectedItems.length;

        dispatch(
          addNotificationAction({
            type: NotificationType.Snack,
            severity: NotificationSeverity.Success,
            message: `Подготовлено к отправке актов: ${updateCount}`,
          })
        );
      } catch (error) {
        const reason = getWriteOffErrorReason(error);

        if (IS_WRITE_OFF_ERROR_MODAL_ENABLED) {
          dispatch(
            addNotificationAction({
              type: NotificationType.Confirm,
              confirmOnly: true,
              severity: NotificationSeverity.Error,
              title: "Ошибка списания ТМЦ",
              message: `Не удалось выполнить списание ТМЦ.\n\nПричина: ${reason}`,
              cb: () => void 0,
              error,
            })
          );
        } else {
          dispatch(
            addNotificationAction({
              type: NotificationType.Snack,
              severity: NotificationSeverity.Error,
              message: "Возникла ошибка при попытке списания актов",
              error,
            })
          );
        }
        updateCount = 0;
      }

      // marker of changes (records updated or not)
      return !!updateCount;
    };

    return new Promise((resolve, reject) => {
      dispatch(
        addNotificationAction({
          type: NotificationType.Confirm,
          severity: NotificationSeverity.Info,
          message: "Внимание! Будут списаны все выбранные ТМЦ (из агрегирующей таблицы)",
          cb: (err) => {
            if (err) {
              resolve(false);
              return;
            }
            prepareAssetsToSendCb().then(resolve).catch(reject);
          },
        })
      );
    });
  }
);

// TODO: need an integration with react query (list, count, filter) instead of using this store
// export const assetUsageRecordsApi = createApi({
//     reducerPath: 'api-asset-usage-records',
//     tagTypes: ['AssetUsageRecords'],
//     baseQuery,
//     endpoints: (build) => ({
//         assetUsageRecordList: build.query<AssetUsageRecord[], TAssetUsageFilter>({
//             query: (filter)=>({
//                 url: 'AssetUsageRecords',
//                 method: 'GET',
//                 searchParams: { ...filter },
//             }),
//             providesTags: ['AssetUsageRecords'],
//             onCacheEntryAdded: (_arg, {dispatch, getState}): Promise<void> | void =>  {
//                 const state = getState();
//                 console.log(state);
//                 let dtos: IAssetUsageRecordDto[]=[];
//                 try {
//                     verificationService.check({
//                         cacheId: "AssetUsageRecordListSchema",
//                         schema: AssetUsageRecordListSchema,
//                         message: "Невалидный ответ сервера (AssetUsageRecord)",
//                     })(dtos)
//                 } catch (error) {
//                     dispatch(
//                         addNotificationAction({
//                             type: NotificationType.Snack,
//                             severity: NotificationSeverity.Error,
//                             message: "Ошибка получения актов списания",
//                             error,
//                         })
//                     );
//                     throw error;
//                 }
//
//             },
//             transformResponse: (data: IAssetUsageRecordDto[]) => verificationService
//                 .check({
//                     cacheId: "AssetUsageRecordListSchema",
//                     schema: AssetUsageRecordListSchema,
//                     message: "Невалидный ответ сервера (AssetUsageRecord)",
//                 })(data)
//                 .map((dto) => {
//                     const model = new AssetUsageRecord(dto.id);
//                     model.updateFromDto(dto);
//                     return model;
//                 })
//         })
//     })
// })
//
// export const {useAssetUsageRecordListQuery} = assetUsageRecordsApi;
